/*
 * The composer's copy. Every string the composer shows has a key here and nowhere else (Q38);
 * the shell merges this catalogue into the application's (modules.md §8).
 */

export const en = {
	"composer.send": "Send",
	// The joined chevron of a send button that has other ways to send (an AI chat's Queue ▾).
	"composer.send.more": "More ways to send",
	"composer.cancel": "Cancel",
	"composer.tooLongCount": "This message is {count} of {max} characters — please shorten it.",
	// Permanent chrome under every composer, so one clause each: what sends, and what starts a
	// line. The code-block rule stays in the description, which is read out but takes no room.
	"composer.hint.enterSends": "Enter sends · Shift+Enter starts a new line",
	"composer.hint.shiftEnterSends": "Shift+Enter sends · Enter starts a new line",
	"composer.hint.codeBlock":
		"Inside a code block Enter starts a new line. Ctrl+Enter sends from anywhere.",
	// T-08 — the third state of the line, drawn while the caret is in a list: there Enter belongs to
	// the list whatever the setting says, so the sentence names the key that still posts (X-B1 A7).
	"composer.hint.list": "Enter adds an item · Ctrl+Enter sends",
	// What a touch device does instead: the line is never drawn there, and this is the sentence a
	// screen reader is given in its place.
	"composer.hint.touch": "The send button sends; Enter starts a new line.",
	"composer.preview.show": "Show preview",
	"composer.preview.hide": "Hide preview",
	"composer.preview.title": "Preview",
	"composer.preview.empty": "Nothing to show yet.",
	"composer.preview.failed": "The preview could not be loaded.",
	"composer.sendFailed": "Not sent: {reason}",
	"composer.draft.tooLarge":
		"This draft is too large to be stored on this device. The text stays in the field until the tab is closed.",
	"composer.suggestions": "Suggestions",
	// The keys a suggestion list answers, on the footer line of a list whose source has one.
	"composer.suggestions.keys": "↑ ↓ to move · Enter to choose · Esc to close",
	"composer.suggestion.group": "Group · {count} members",
	"composer.suggestion.group.one": "Group · {count} member",
	"composer.placeholder.bold": "bold",
	"composer.placeholder.italic": "italic",
	"composer.placeholder.strike": "struck through",
	"composer.placeholder.code": "code",
	"composer.placeholder.linkText": "link text",
	"composer.placeholder.url": "https://",

	// The attachment tray. Every sentence names the file it is about: a batch of ten that fails as
	// one line tells nobody which one to take out.
	"composer.attach.button": "Attach files",
	"composer.attach.tray": "Files to send",
	"composer.attach.chip": "{name}, {size}, file {position} of {total}",
	"composer.attach.queued": "waiting to upload",
	"composer.attach.uploading": "uploading, {percent}%",
	"composer.attach.failed": "upload failed",
	"composer.attach.progress": "Upload of {name}",
	"composer.attach.remove": "Remove",
	"composer.attach.removeNamed": "Remove {name}",
	"composer.attach.removed": "{name} removed",
	"composer.attach.moveLeft": "Move left",
	"composer.attach.moveRight": "Move right",
	"composer.attach.moved": "{name} is now file {position} of {total}",
	"composer.attach.retry": "Try again",
	"composer.attach.cancelled": "This upload was cancelled.",
	"composer.attach.dropHere": "Drop the files here",
	"composer.attach.sendWhenReady": "Send when the uploads finish",
	// The chip of a paste too long for the box, which became a text file (S12 §7.3).
	"composer.attach.pasted": "Pasted text · {lines} lines",
	"composer.attach.pasted.one": "Pasted text · {lines} line",
	"composer.attach.rename.label": "File name",
	"composer.attach.caption.open": "Caption and name",
	"composer.attach.caption.title": "Caption for {name}",
	"composer.attach.caption.label": "Caption",
	"composer.attach.caption.hint": "Describe the file for people who cannot see it.",
	"composer.attach.caption.save": "Save",
	"composer.attach.error.tooLarge": "{name} is larger than {limit} and was not attached.",
	"composer.attach.error.tooMany":
		"A message carries at most {limit} files, so {name} was left out.",
	"composer.attach.error.messageTooLarge":
		"One message carries at most {limit} in files, so {name} was left out.",

	// E-2 — the formatted editor: the toolbar, its overflow menu, the link dialog and the
	// announcements. The editor's accessible name is not a key of its own: it is the placeholder.
	"composer.emoji.button": "Emoji",
	"composer.format.bold": "Bold",
	"composer.format.bullet": "Bulleted list",
	"composer.format.code": "Code",
	"composer.format.codeblock": "Code block",
	"composer.format.hide": "Hide formatting",
	"composer.format.italic": "Italic",
	"composer.format.link": "Link",
	"composer.format.ordered": "Numbered list",
	"composer.format.quote": "Quote",
	"composer.format.show": "Show formatting",
	"composer.format.strike": "Strikethrough",
	"composer.format.toolbar": "Formatting",
	"composer.link.cancel": "Cancel",
	"composer.link.insert": "Insert",
	"composer.link.inserted": "Link inserted",
	"composer.link.refused": "Only http, https and mailto links can be posted.",
	"composer.link.remove": "Remove link",
	"composer.link.removed": "Link removed",
	"composer.link.save": "Save",
	"composer.link.text": "Text",
	"composer.link.title": "Link",
	"composer.link.url": "URL",
	"composer.menu.copiedPlain": "Copied as plain text",
	"composer.menu.copyPlain": "Copy as plain text",
	"composer.menu.more": "More",
	"composer.menu.shortcuts": "Keyboard shortcuts",
	"composer.menu.sourceMode": "Edit as markdown",

	// Q-04 — the frame: the names of the live regions the composer owns, so a reader's list of
	// regions says what each one is for instead of listing three anonymous "status" entries
	// (X-A2 P12).
	"composer.status.draft": "Draft storage",
	"composer.status.refused": "Why this message cannot be sent",
} as const satisfies Record<string, string>
